import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { uploadCustomerLogo } from "../api/Customer.api";

interface Customer {
  id?: string;
  name: string;
  customerId: string;
  createdAt: string;
  active: boolean;
  mainUserName?: string;
  mainUserPhone?: string;
  mainUserEmail?: string;
  logo?: string;
  instagramUrl?: string;
  identifier: string;
  showWork: boolean;
  termsAndConditions: boolean;
}

interface CustomerModalProps {
  isOpen: boolean;
  onClose: () => void;
  customerId: string;
  customerData: Customer[];
  onConfirm: (customer: Customer) => void;
}

const CustomerModal = ({
  isOpen,
  onClose,
  customerId,
  customerData,
  onConfirm,
}: CustomerModalProps) => {
  const REACT_APP_LOGO_MAX_SIZE_MB = 10;
  const REACT_APP_LOGO_MAX_RES_WIDTH = 400;
  const REACT_APP_LOGO_MAX_RES_HEIGHT = 400;
  const REACT_APP_LOGO_ALLOWED_EXTENSIONS = ".png,.jpg,.jpeg,.pdf";

  const defaultCustomer: Customer = {
    name: "",
    customerId: "",
    createdAt: "",
    active: false,
    mainUserName: "",
    mainUserPhone: "",
    mainUserEmail: "",
    logo: "",
    instagramUrl: "",
    identifier: "",
    showWork: false,
    termsAndConditions: false,
  };

  
  const [customer, setCustomer] = useState<Customer>(defaultCustomer);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [blocked, setBlocked] = useState(true);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  
  useEffect(() => {
    if (customerData && customerData.length > 0) {
      const dataToUse = customerData[0];
      const normalizedInstagram = dataToUse.instagramUrl
        ? dataToUse.instagramUrl.replace("https://www.instagram.com/", "")
        : "";

      setCustomer({
        ...dataToUse,
        instagramUrl: normalizedInstagram,
      });
    }
  }, [customerData]);

  const fieldMetadata: Record<
    string,
    {
      label: string;
      description: string;
      type?: "text" | "number" | "boolean" | "select";
      options?: string[];
      readOnly?: boolean;
    }
  > = {
    name: {
      label: "Nombre de la empresa*",
      description: "Nombre de la empresa",
      type: "text",
      readOnly: false,
    },
    instagramUrl: {
      label: "Instagram",
      description: "Instagram",
      type: "text",
      readOnly: false,
    },
    mainUserName: {
      label: "Usuario principal",
      description: "Usuario principal",
      type: "text",
      readOnly: true,
    },
    mainUserPhone: {
      label: "Teléfono principal",
      description: "Teléfono principal",
      type: "text",
      readOnly: true,
    },
    mainUserEmail: {
      label: "Email principal",
      description: "Email principal",
      type: "text",
      readOnly: true,
    },
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCustomer((prevCustomer) => ({
      ...prevCustomer,
      [name]: value,
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (file) {
      const isImage = file.type.startsWith("image/");
      const fileExtension = "." + file.name.split(".").pop()?.toLowerCase();

      if (
        !REACT_APP_LOGO_ALLOWED_EXTENSIONS.split(",").includes(fileExtension)
      ) {
        toast.error(
          `Tipo de archivo no válido. Extensiones permitidas: ${REACT_APP_LOGO_ALLOWED_EXTENSIONS}`
        );
        setLogoFile(null);
        return;
      }

      if (file.size > REACT_APP_LOGO_MAX_SIZE_MB * 1024 * 1024) {
        toast.error(
          `El archivo excede el tamaño máximo de ${REACT_APP_LOGO_MAX_SIZE_MB}MB`
        );
        setLogoFile(null);
        return;
      }

      if (isImage) {
        const reader = new FileReader();
        reader.onload = (event) => {
          const img = new Image();
          img.onload = () => {
            if (
              img.width > REACT_APP_LOGO_MAX_RES_WIDTH ||
              img.height > REACT_APP_LOGO_MAX_RES_HEIGHT
            ) {
              toast.error(
                `La imagen debe tener una resolución máxima de ${REACT_APP_LOGO_MAX_RES_WIDTH}x${REACT_APP_LOGO_MAX_RES_HEIGHT}px.`
              );
              setLogoFile(null);
            } else {
              setLogoFile(file);
            }
          };
          img.src = event.target?.result as string;
        };
        reader.readAsDataURL(file);
      } else {
        setLogoFile(file);
      }
    } else {
      setLogoFile(null);
    }
  };

  const handleSubmit = async () => {
    setBlocked(true);
    try {
      const customerToSave = {
        ...customer,
        instagramUrl: customer.instagramUrl
          ? `https://www.instagram.com/${customer.instagramUrl}`
          : "",
      };

      console.log("Enviando al backend:", customerToSave);

      onConfirm(customerToSave);
      onClose();
      setBlocked(false);
    } catch (error: any) {
      console.error("Error al editar el cliente", error);
      toast.error(error.message || "Hubo un error al actualizar el cliente");
      setBlocked(false);
    }
  };

  const handleUploadLogo = async () => {
    if (!logoFile) {
      toast.error("Por favor, selecciona un archivo para subir.");
      return;
    }

    setUploadingLogo(true);
    console.log(customerData)
    try {
      const response = await uploadCustomerLogo(customerData, logoFile);
      toast.success(response.message || "Logo actualizado exitosamente");
      onConfirm(response);
      setLogoFile(null);
    } catch (error: any) {
      console.error("Error al subir el logo", error);
      toast.error(error.message || "Hubo un error al subir el logo.");
    } finally {
      setUploadingLogo(false);
      window.location.reload(); 
    }
  };

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    if (isOpen) {
      window.addEventListener("keydown", handleEsc);
      return () => window.removeEventListener("keydown", handleEsc);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const getPlaceholder = (name: string) => {
    switch (name) {
      case "name":
        return "Mi Empresa S.A.";
      case "instagramUrl":
        return "miempresa";
      case "mainUserName":
        return "Juan Pérez";
      case "mainUserPhone":
        return "5491112345678";
      case "mainUserEmail":
        return "juan.perez@miempresa.com";
      default:
        return "";
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 z-50 overflow-y-auto"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="relative bg-white rounded-lg w-full max-w-xl mt-16 mb-8">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-400 hover:text-gray-500"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <div className="p-6 space-y-4 max-h-[80vh] overflow-y-auto">
          <p>
            <strong>Edición de cliente</strong>
          </p>
          <p>Complete su información</p>

          {Object.keys(fieldMetadata).map((name) => (
            <div key={name}>
              <label className="block font-medium text-sm mb-1">
                {fieldMetadata[name]?.label || name}
              </label>
              {name === "instagramUrl" ? (
                <div
                  className={`flex items-center border border-gray-300 rounded px-3 py-2 ${
                    fieldMetadata[name]?.readOnly ? "bg-gray-100" : ""
                  }`}
                >
                  <span className="text-gray-500">https://instagram.com/</span>
                  <input
                    type="text"
                    name={name}
                    value={customer.instagramUrl || ""}
                    onChange={(e) =>
                      setCustomer((prev) => ({
                        ...prev,
                        instagramUrl: e.target.value,
                      }))
                    }
                    placeholder={getPlaceholder(name)}
                    readOnly={fieldMetadata[name]?.readOnly}
                    className={`flex-grow focus:outline-none ${
                      fieldMetadata[name]?.readOnly ? "bg-gray-100" : ""
                    }`}
                  />
                </div>
              ) : (
                <input
                  type={fieldMetadata[name]?.type || "text"}
                  name={name}
                  value={
                    customer[name as keyof Customer] !== undefined &&
                    customer[name as keyof Customer] !== null
                      ? String(customer[name as keyof Customer])
                      : ""
                  }
                  onChange={handleInputChange}
                  placeholder={getPlaceholder(name)}
                  readOnly={fieldMetadata[name]?.readOnly}
                  className={`w-full border border-gray-300 rounded px-3 py-2 ${
                    fieldMetadata[name]?.readOnly ? "bg-gray-100" : ""
                  }`}
                />
              )}
            </div>
          ))}

          <div className="mt-4">
            <label className="block font-medium text-sm mb-1">Subir Logo</label>
            <p className="text-xs text-gray-500">
              En medida de 400px x 400px, fondo transparente (preferentemente)
            </p>
            <input
              type="file"
              accept={REACT_APP_LOGO_ALLOWED_EXTENSIONS}
              onChange={handleFileChange}
              className="w-full text-sm text-gray-500
                file:mr-4 file:py-2 file:px-4
                file:rounded-full file:border-0
                file:text-sm file:font-semibold
                file:bg-blue-50 file:text-blue-700
                hover:file:bg-blue-100"
            />
            {logoFile && (
              <div className="mt-2 flex flex-col sm:flex-row items-start sm:items-center justify-end">
                <button
                  onClick={handleUploadLogo}
                  disabled={uploadingLogo}
                  className={`px-4 py-2 text-white rounded font-semibold transition-colors duration-200 ${
                    uploadingLogo
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-green-500 hover:bg-green-600"
                  }`}
                >
                  {uploadingLogo ? "Subiendo..." : "Subir Logo"}
                </button>
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 rounded"
            >
              Cancelar
            </button>
            <button
              onClick={handleSubmit}
              className={`px-4 py-2 ${
                blocked ? "bg-gray-400 cursor-not-allowed" : "bg-blue-500"
              } text-white rounded`}
              disabled={blocked}
            >
              {blocked ? "Próximamente" : "Actualizar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerModal;
